import {
  conditionSourceKey,
  getMissingUserDataDefault,
  isAnswerConditionMet,
  normalizeConditionSource,
  normalizeConditionalRules,
  type ConditionSource,
} from "./conditionalRules";
import type {
  NavigationPageDisplayClasses,
  NavigationPageDisplaySettings,
  NavigationPageDisplayState,
  NavigationPageItem,
  NavigationPageItemVariant,
  NavigationPageVariantComposition,
  NavigationPageVariantPreset,
} from "../types/blocks/navigation/NavigationPageOption";

export interface ResolvedNavigationPageItem extends NavigationPageItem {
  activeVariantId: string | null;
  displayStateId: string;
  preset?: NavigationPageVariantPreset;
  interactive: boolean;
  classes: Required<NavigationPageDisplayClasses>;
}

export interface NavigationConditionEvaluationTrace {
  ruleId?: string;
  index: number;
  source: ConditionSource | null;
  sourceKey: string | null;
  available: boolean;
  actualValue?: unknown;
  actualType?: string;
  expectedValue: unknown;
  expectedType: string;
  operator: string;
  result: boolean | null;
  reason?: "invalid-source" | "unavailable";
  usedMissingUserDataFallback?: boolean;
}

export interface NavigationVariantEvaluationTrace {
  id: string;
  name: string;
  composition: NavigationPageVariantComposition;
  evaluated: boolean;
  matched: boolean;
  skippedReason?: "earlier-variant-matched";
  conditions: NavigationConditionEvaluationTrace[];
}

export interface NavigationPageItemEvaluationTrace {
  itemId: string;
  itemTitle: string;
  activeVariantId: string | null;
  variants: NavigationVariantEvaluationTrace[];
}

export const DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID = "default";

export const BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES: NavigationPageDisplayState[] = [
  {
    id: "default",
    name: "Default",
    interactive: true,
    classes: {
      card: "",
      description: "",
      icon: "",
      interactive: "group-hover:bg-accent/20",
    },
  },
  {
    id: "current",
    name: "Current",
    interactive: true,
    classes: {
      card: "border-primary bg-primary text-primary-foreground",
      description: "text-primary-foreground/80",
      icon: "text-primary-foreground",
      interactive: "group-hover:bg-primary/90",
    },
  },
  {
    id: "completed",
    name: "Completed",
    interactive: true,
    classes: {
      card: "border-success/40 bg-muted",
      description: "",
      icon: "text-success",
      interactive: "group-hover:bg-muted/80",
    },
  },
  {
    id: "locked",
    name: "Locked",
    interactive: true,
    classes: {
      card: "border-border bg-muted/60 text-muted-foreground",
      description: "text-muted-foreground",
      icon: "text-muted-foreground",
      interactive: "group-hover:bg-muted/80",
    },
  },
];

const EMPTY_CLASSES: Required<NavigationPageDisplayClasses> = {
  card: "",
  description: "",
  icon: "",
  interactive: "",
};

const LEGACY_PRESET_IDS = new Set<NavigationPageVariantPreset>([
  "default",
  "current",
  "completed",
  "locked",
]);

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function optionalClass(value: unknown): string | undefined {
  return nonEmptyString(value);
}

function normalizeDisplayClasses(value: unknown): NavigationPageDisplayClasses | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const candidate = value as Partial<NavigationPageDisplayClasses>;
  const classes = Object.fromEntries(
    (["card", "description", "icon", "interactive"] as const)
      .map((key) => [key, optionalClass(candidate[key])] as const)
      .filter((entry): entry is readonly [
        keyof NavigationPageDisplayClasses,
        string,
      ] => entry[1] !== undefined),
  ) as NavigationPageDisplayClasses;
  return Object.keys(classes).length ? classes : undefined;
}

function normalizeDisplaySettings(
  value: Partial<NavigationPageDisplaySettings>,
): NavigationPageDisplaySettings {
  return {
    title: nonEmptyString(value.title),
    description: nonEmptyString(value.description),
    icon: nonEmptyString(value.icon),
    iconColor: nonEmptyString(value.iconColor),
    image: nonEmptyString(value.image),
    url: nonEmptyString(value.url),
    to: nonEmptyString(value.to),
    external: optionalBoolean(value.external),
    interactive: optionalBoolean(value.interactive),
    classes: normalizeDisplayClasses(value.classes),
  };
}

export function normalizeNavigationPageDisplayState(
  value: unknown,
): NavigationPageDisplayState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<NavigationPageDisplayState>;
  const id = nonEmptyString(candidate.id);
  if (!id) return null;
  return {
    id,
    name: nonEmptyString(candidate.name) ?? "Display state",
    ...normalizeDisplaySettings(candidate),
  };
}

export function normalizeNavigationPageDisplayStates(
  value: unknown,
): NavigationPageDisplayState[] {
  if (value === undefined) {
    return BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES.map((state) => ({
      ...state,
      classes: { ...state.classes },
    }));
  }
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map(normalizeNavigationPageDisplayState)
    .filter((state): state is NavigationPageDisplayState => {
      if (!state || seen.has(state.id)) return false;
      seen.add(state.id);
      return true;
    });
}

export function normalizeNavigationPageVariant(
  value: unknown,
): NavigationPageItemVariant | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<NavigationPageItemVariant>;
  const id = nonEmptyString(candidate.id);
  if (!id) return null;

  const composition: NavigationPageVariantComposition =
    candidate.composition === "any" ? "any" : "all";
  const displayStateId =
    nonEmptyString(candidate.displayStateId)
    ?? nonEmptyString(candidate.preset)
    ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID;

  return {
    id,
    name: nonEmptyString(candidate.name) ?? "State variant",
    composition,
    conditions: normalizeConditionalRules(candidate.conditions).map((rule) => ({
      ...rule,
      action: undefined,
    })),
    displayStateId,
    preset: candidate.preset,
    ...normalizeDisplaySettings(candidate),
  };
}

export function normalizeNavigationPageVariants(
  value: unknown,
): NavigationPageItemVariant[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeNavigationPageVariant)
    .filter((variant): variant is NavigationPageItemVariant => variant !== null);
}

export function getNavigationPageDisplayStateReferences(
  items: NavigationPageItem[],
  stateId: string,
): string[] {
  const references: string[] = [];
  items.forEach((item, itemIndex) => {
    if ((item.displayStateId ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID) === stateId) {
      references.push(item.title || `Item ${itemIndex + 1}`);
    }
    (item.variants ?? []).forEach((variant, variantIndex) => {
      const variantStateId =
        variant.displayStateId
        ?? variant.preset
        ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID;
      if (variantStateId === stateId) {
        references.push(
          `${item.title || `Item ${itemIndex + 1}`} / ${variant.name || `Variant ${variantIndex + 1}`}`,
        );
      }
    });
  });
  return references;
}

export function variantMatches(
  variant: NavigationPageItemVariant,
  resolveValue: (source: ConditionSource) => unknown,
  isAvailable: (source: ConditionSource) => boolean,
): boolean {
  return evaluateNavigationPageVariant(
    variant,
    resolveValue,
    isAvailable,
  ).matched;
}

export function evaluateNavigationPageVariant(
  variant: NavigationPageItemVariant,
  resolveValue: (source: ConditionSource) => unknown,
  isAvailable: (source: ConditionSource) => boolean,
): NavigationVariantEvaluationTrace {
  const rules = normalizeConditionalRules(variant.conditions);
  if (!rules.length) {
    return {
      id: variant.id,
      name: variant.name,
      composition: variant.composition,
      evaluated: true,
      matched: false,
      conditions: [],
    };
  }

  const conditions = rules.map((rule, index) => {
    const source = normalizeConditionSource(rule);
    const base = {
      ruleId: rule.id,
      index,
      source,
      sourceKey: source ? conditionSourceKey(source) : null,
      expectedValue: rule.value,
      expectedType: typeof rule.value,
      operator: String(rule.condition ?? ""),
    };
    if (!source) {
      return {
        ...base,
        available: false,
        result: null,
        reason: "invalid-source" as const,
      };
    }
    if (!isAvailable(source)) {
      const missingDefault = getMissingUserDataDefault(source);
      if (missingDefault !== undefined) {
        const result = isAnswerConditionMet(
          missingDefault,
          rule.condition,
          rule.value,
        );
        return {
          ...base,
          available: false,
          actualValue: missingDefault,
          actualType: typeof missingDefault,
          result,
          usedMissingUserDataFallback: true,
        };
      }
      return {
        ...base,
        available: false,
        result: null,
        reason: "unavailable" as const,
      };
    }
    const actualValue = resolveValue(source);
    const result = isAnswerConditionMet(
      actualValue,
      rule.condition,
      rule.value,
    );
    return {
      ...base,
      available: true,
      actualValue,
      actualType: typeof actualValue,
      result,
    };
  });
  const evaluations = conditions.map((condition) => condition.result);
  const matched = evaluations.some((result) => result === null)
    ? false
    : variant.composition === "any"
      ? evaluations.some(Boolean)
      : evaluations.every(Boolean);

  return {
    id: variant.id,
    name: variant.name,
    composition: variant.composition,
    evaluated: true,
    matched,
    conditions,
  };
}

export function resolveNavigationPageItem(
  item: NavigationPageItem,
  resolveValue: (source: ConditionSource) => unknown,
  isAvailable: (source: ConditionSource) => boolean,
  onEvaluation?: (trace: NavigationPageItemEvaluationTrace) => void,
  configuredDisplayStates?: unknown,
): ResolvedNavigationPageItem {
  const variants = normalizeNavigationPageVariants(item.variants);
  const displayStates = normalizeNavigationPageDisplayStates(configuredDisplayStates);
  const fallbackState =
    displayStates.find((state) => state.id === DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID)
    ?? BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES[0];
  const findDisplayState = (id: string | undefined) =>
    displayStates.find((state) => state.id === id) ?? fallbackState;
  const traces: NavigationVariantEvaluationTrace[] = [];
  let activeVariant: NavigationPageItemVariant | undefined;
  for (const variant of variants) {
    if (activeVariant) {
      traces.push({
        id: variant.id,
        name: variant.name,
        composition: variant.composition,
        evaluated: false,
        matched: false,
        skippedReason: "earlier-variant-matched",
        conditions: [],
      });
      continue;
    }

    const trace = evaluateNavigationPageVariant(
      variant,
      resolveValue,
      isAvailable,
    );
    traces.push(trace);
    if (trace.matched) activeVariant = variant;
  }
  onEvaluation?.({
    itemId: item.id,
    itemTitle: item.title,
    activeVariantId: activeVariant?.id ?? null,
    variants: traces,
  });
  const baseDisplayState = findDisplayState(
    nonEmptyString(item.displayStateId) ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID,
  );
  const activeDisplayState = activeVariant
    ? findDisplayState(activeVariant.displayStateId)
    : undefined;
  const mergeSettings = (
    target: NavigationPageDisplaySettings,
    source: NavigationPageDisplaySettings | undefined,
  ) => {
    if (!source) return target;
    const destination = source.url ?? source.to;
    const next = {
      ...target,
      ...Object.fromEntries(
        ([
          "title",
          "description",
          "icon",
          "iconColor",
          "image",
          "external",
          "interactive",
        ] as const)
          .filter((key) => source[key] !== undefined)
          .map((key) => [key, source[key]]),
      ),
      classes: {
        ...target.classes,
        ...source.classes,
      },
    };
    if (destination !== undefined) {
      next.url = destination;
      next.to = destination;
    }
    return next;
  };
  let resolvedSettings = mergeSettings({}, baseDisplayState);
  resolvedSettings = mergeSettings(resolvedSettings, normalizeDisplaySettings(item));
  resolvedSettings = mergeSettings(resolvedSettings, activeDisplayState);
  resolvedSettings = mergeSettings(resolvedSettings, activeVariant);
  const displayStateId = activeDisplayState?.id ?? baseDisplayState.id;

  return {
    ...item,
    ...resolvedSettings,
    variants,
    activeVariantId: activeVariant?.id ?? null,
    displayStateId,
    preset:
      activeVariant?.preset
      ?? (LEGACY_PRESET_IDS.has(displayStateId as NavigationPageVariantPreset)
        ? displayStateId as NavigationPageVariantPreset
        : undefined),
    interactive: resolvedSettings.interactive ?? true,
    classes: {
      ...EMPTY_CLASSES,
      ...resolvedSettings.classes,
    },
  };
}
